import React from 'react';
import { Shield, FileText, Lock, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/Schema';

const PrivacyPolicy = () => {
  const lastUpdated = "January 20, 2025";

  return (
    <div className="min-h-screen bg-gray-950">
      <SEO 
        title="Privacy Policy & Legal Disclaimer"
        description="Privacy Policy and Legal Disclaimer for TruckAccidentSupport.com. Learn how we collect, use, and protect your information."
        canonical="/privacy-policy"
        keywords="privacy policy, legal disclaimer, data protection, PIPEDA, CAN-SPAM"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Privacy Policy', path: '/privacy-policy' }
      ]} />

      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-500/10">
                <Shield className="h-8 w-8 text-orange-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Privacy Policy & Legal Disclaimer
            </h1>
            <p className="text-xl text-gray-300">
              Last Updated: {lastUpdated}
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Privacy Policy */}
            <Card className="bg-gray-900 border-gray-800 mb-8">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Lock className="h-6 w-6 text-orange-400" />
                  <CardTitle className="text-white text-2xl">Privacy Policy</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="text-gray-300 space-y-6">
                <p>
                  This Privacy Policy explains how we collect, use, store, and protect personal information from visitors to this website (the "Site"). This policy is intended to comply with applicable privacy laws in both the <strong>United States</strong> and <strong>Canada</strong>, including the Personal Information Protection and Electronic Documents Act (PIPEDA).
                </p>
                <p>
                  By using this Site, you consent to the practices described in this Privacy Policy.
                </p>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">1. Information We Collect</h3>
                  
                  <h4 className="text-lg font-semibold text-orange-400">Personal Information</h4>
                  <p>We may collect personal information that you voluntarily provide, including:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Information submitted through contact forms</li>
                    <li>Information provided when subscribing to newsletters or downloading resources</li>
                  </ul>

                  <h4 className="text-lg font-semibold text-orange-400">Non-Personal Information</h4>
                  <p>We may automatically collect non-identifying information such as:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Browser type</li>
                    <li>Device type</li>
                    <li>IP address</li>
                    <li>Pages visited and time spent on the Site</li>
                    <li>Referring websites</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">2. How We Use Information</h3>
                  <p>We may use collected information to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide and maintain the Site</li>
                    <li>Send newsletters, updates, and educational content</li>
                    <li>Deliver downloadable resources</li>
                    <li>Respond to inquiries and requests</li>
                    <li>Improve Site performance and content</li>
                    <li>Display relevant advertising</li>
                  </ul>
                  <p className="font-semibold text-orange-400">We do NOT sell personal information.</p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">3. Email Communications</h3>
                  <p>
                    By subscribing to our email list, you consent to receive communications from us. You may unsubscribe at any time by using the unsubscribe link included in all emails.
                  </p>
                  <p>Email communications comply with:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>CAN-SPAM Act (United States)</li>
                    <li>CASL – Canada's Anti-Spam Legislation</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">4. Cookies and Tracking Technologies</h3>
                  <p>This Site may use cookies, pixels, and similar technologies to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Improve user experience</li>
                    <li>Analyze traffic and usage patterns</li>
                    <li>Support advertising and analytics services</li>
                  </ul>
                  <p>
                    You may disable cookies through your browser settings; however, some features of the Site may not function properly.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">5. Advertising and Third-Party Services</h3>
                  <p>
                    We may display advertisements or use third-party services such as analytics providers and ad networks.
                  </p>
                  <p>
                    These third parties may collect information through cookies or similar technologies. We do not control their data practices and encourage users to review their privacy policies.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">6. Data Storage and Security</h3>
                  <p>
                    We implement reasonable administrative, technical, and physical safeguards to protect personal information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">7. Access and Correction (Canada)</h3>
                  <p>
                    Canadian users have the right to request access to or correction of their personal information, subject to applicable laws.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">8. Children's Privacy</h3>
                  <p>
                    This Site is not intended for individuals under the age of 18. We do not knowingly collect personal information from minors.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">9. Changes to This Privacy Policy</h3>
                  <p>
                    We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">10. Contact Information</h3>
                  <p>
                    For questions about this Privacy Policy or data practices, please contact us through the Contact page on this Site.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Legal Disclaimer */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-6 w-6 text-orange-400" />
                  <CardTitle className="text-white text-2xl">Legal Disclaimer</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="text-gray-300 space-y-6">
                <p>
                  The information provided on this Site is for <strong>general informational and educational purposes only</strong>.
                </p>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">1. No Legal Advice</h3>
                  <p>
                    Nothing on this Site constitutes legal advice, and no attorney-client relationship is formed by your use of this Site. Laws and regulations vary by jurisdiction and change over time.
                  </p>
                  <p>
                    You should consult a qualified attorney or appropriate professional in your jurisdiction for advice specific to your situation.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">2. No Safety or Professional Advice</h3>
                  <p>
                    Content related to truck safety, accident prevention, compliance, or operations is provided for general educational purposes only. The Site does not provide professional safety, regulatory, medical, or operational advice.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">3. Accuracy of Information</h3>
                  <p>
                    While we strive to provide accurate and up-to-date information, we make no representations or warranties regarding the completeness, accuracy, or reliability of any content.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">4. Limitation of Liability</h3>
                  <p>
                    To the fullest extent permitted by law in both the United States and Canada, we disclaim all liability for any loss, injury, damage, or legal consequences arising from reliance on information provided on this Site.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">5. External Links</h3>
                  <p>
                    This Site may contain links to third-party websites. We do not control or endorse the content of external sites and are not responsible for their accuracy or practices.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">6. Testimonials and Examples</h3>
                  <p>
                    Any examples, case discussions, or testimonials presented are illustrative only and do not guarantee similar outcomes.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">7. Use at Your Own Risk</h3>
                  <p>
                    Your use of this Site and its content is entirely at your own risk.
                  </p>
                </div>

                <div className="mt-8 p-6 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-center font-semibold text-white">
                    By using this Site, you acknowledge and agree to this Privacy Policy and Legal Disclaimer.
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
